// Status Badge Component — Direct conversion from HTML design system
import React from 'react';
import { cn } from '@/lib/utils';

type Status = 'active' | 'expired' | 'revoked' | 'suspended' | 'trial';

interface StatusBadgeProps {
  status: Status | string;
  className?: string;
}

const statusConfig: Record<string, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-green-100 text-green-800' },
  expired: { label: 'Expired', className: 'bg-red-100 text-red-700' },
  revoked: { label: 'Revoked', className: 'bg-red-200 text-red-900' },
  suspended: { label: 'Suspended', className: 'bg-yellow-100 text-yellow-800' },
  trial: { label: 'Trial', className: 'bg-purple-100 text-purple-800' },
  paid: { label: 'Paid', className: 'bg-green-100 text-green-800' },
  pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-700' },
  canceled: { label: 'Canceled', className: 'bg-gray-100 text-gray-600' },
  past_due: { label: 'Past Due', className: 'bg-orange-100 text-orange-800' },
  low: { label: 'Low', className: 'bg-green-100 text-green-800' },
  medium: { label: 'Medium', className: 'bg-yellow-100 text-yellow-800' },
  high: { label: 'High', className: 'bg-orange-100 text-orange-800' },
  critical: { label: 'Critical', className: 'bg-red-100 text-red-800' },
  // 18H §4 — new status keys, additive (console's own config, not shared with
  // TPRM7's vw-kit). invite: pending/registered/expired/suppressed.
  registered: { label: 'Registered', className: 'bg-green-100 text-green-800' },
  // opportunity: open/nudged/converted/dismissed.
  open: { label: 'Open', className: 'bg-blue-100 text-blue-800' },
  nudged: { label: 'Nudged', className: 'bg-purple-100 text-purple-800' },
  converted: { label: 'Converted', className: 'bg-green-100 text-green-800' },
  dismissed: { label: 'Dismissed', className: 'bg-gray-100 text-gray-500' },
  // ledger: saas/install/platform.
  saas: { label: 'SaaS', className: 'bg-indigo-100 text-indigo-800' },
  install: { label: 'Install', className: 'bg-gray-100 text-gray-700' },
  platform: { label: 'Platform', className: 'bg-blue-100 text-blue-800' },
  // dispute: open/window_expired/resolved.
  window_expired: { label: 'Window Expired', className: 'bg-red-100 text-red-700' },
  resolved: { label: 'Resolved', className: 'bg-green-100 text-green-800' },
  suppressed: { label: 'Suppressed', className: 'bg-gray-100 text-gray-500' },
  // lead (trust_center_leads, GAP-2 Concierge Queue): new/contacted/provisioning/
  // provisioned/dismissed (dismissed already covered above).
  new: { label: 'New', className: 'bg-blue-100 text-blue-800' },
  contacted: { label: 'Contacted', className: 'bg-indigo-100 text-indigo-800' },
  provisioning: { label: 'Provisioning', className: 'bg-yellow-100 text-yellow-800' },
  provisioned: { label: 'Provisioned', className: 'bg-green-100 text-green-800' },
};

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const config = statusConfig[status.toLowerCase()] || {
    label: status.charAt(0).toUpperCase() + status.slice(1),
    className: 'bg-gray-100 text-gray-600',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide',
        config.className,
        className
      )}
    >
      {config.label}
    </span>
  );
}
